import React, {useMemo, useState} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {InputTextarea} from "primereact/inputtextarea";
import {InputSwitch} from "primereact/inputswitch";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {AI_ERROR_CODES} from "../../../models/ai/ai";
import {useAi} from "../../../models/ai/use-ai";
import {buildProductBriefAiPrompt} from "../utils/build-product-brief-ai-prompt";
import {parseProductBriefAiResponse} from "../utils/parse-product-brief-ai-response";

export default function ProductBriefAiDialog({visible, onHide, translate, brief, workspaceId, onApply}) {
    const {sendMessage} = useAi();
    const [description, setDescription] = useState("");
    const [includeCurrentBrief, setIncludeCurrentBrief] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errorKey, setErrorKey] = useState("");
    const [rawResponse, setRawResponse] = useState("");
    const [generatedBrief, setGeneratedBrief] = useState(null);

    const canSubmit = !!workspaceId && !!description.trim() && !loading;

    const resultSummary = useMemo(() => {
        if (!generatedBrief) return "";
        return [
            generatedBrief.featureName,
            generatedBrief.requestType,
            generatedBrief.priority,
        ].filter(Boolean).join(" / ");
    }, [generatedBrief]);

    const resetResult = () => {
        setErrorKey("");
        setRawResponse("");
        setGeneratedBrief(null);
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        resetResult();
        setLoading(true);
        try {
            const message = buildProductBriefAiPrompt({description, brief, includeCurrentBrief});
            const response = await sendMessage({workspaceId, message});
            const parsed = parseProductBriefAiResponse(response);
            setRawResponse(parsed.rawText || "");
            if (!parsed.success) {
                setErrorKey(parsed.errorKey);
                return;
            }
            setGeneratedBrief(parsed.brief);
        } catch (error) {
            setErrorKey(mapAiErrorToTranslationKey(error?.code));
        } finally {
            setLoading(false);
        }
    };

    const handleApply = () => {
        if (!generatedBrief) return;
        onApply(generatedBrief);
        resetResult();
        setDescription("");
    };

    const footer = (
        <div className="pbb-ai-dialog-footer">
            <Button type="button" label={translate("pbb-cancel")} text onClick={onHide} disabled={loading}/>
            {generatedBrief && (
                <Button type="button" icon="pi pi-check" label={translate("pbb-ai-apply")} onClick={handleApply}/>
            )}
            <Button
                type="button"
                icon="pi pi-send"
                label={translate("pbb-ai-submit")}
                onClick={handleSubmit}
                disabled={!canSubmit}
                loading={loading}
            />
        </div>
    );

    return (
        <Dialog className="pbb-ai-dialog rtl" header={translate("pbb-ai-dialog-title")} visible={visible} onHide={onHide} footer={footer} modal>
            <div className="pbb-ai-dialog-content">
                <p className="pbb-ai-description">{translate("pbb-ai-dialog-description")}</p>

                {!workspaceId && <Message severity="warn" text={translate("pbb-ai-no-workspace")} className="w-full text-xs"/>}
                {errorKey && <Message severity="error" text={translate(errorKey)} className="w-full text-xs"/>}

                <label className="pbb-field">
                    <span>{translate("pbb-ai-input-label")}</span>
                    <InputTextarea
                        autoResize
                        rows={6}
                        value={description}
                        onChange={(event) => {
                            setDescription(event.target.value);
                            resetResult();
                        }}
                        placeholder={translate("pbb-ai-input-placeholder")}
                    />
                </label>

                <div className="pbb-ai-switch-row">
                    <InputSwitch checked={includeCurrentBrief} onChange={(event) => setIncludeCurrentBrief(event.value)}/>
                    <span>{translate("pbb-ai-use-current-brief")}</span>
                </div>

                {loading && (
                    <div className="pbb-ai-loading">
                        <ProgressSpinner className="w-2rem h-2rem"/>
                        <span>{translate("pbb-ai-loading")}</span>
                    </div>
                )}

                {generatedBrief && (
                    <div className="pbb-ai-result">
                        <strong>{translate("pbb-ai-result-ready")}</strong>
                        <span>{resultSummary || translate("pbb-ai-result-ready-description")}</span>
                    </div>
                )}

                {rawResponse && !generatedBrief && (
                    <label className="pbb-field">
                        <span>{translate("pbb-ai-raw-response")}</span>
                        <InputTextarea value={rawResponse} rows={5} readOnly/>
                    </label>
                )}
            </div>
        </Dialog>
    );
}

function mapAiErrorToTranslationKey(code) {
    switch (code) {
        case AI_ERROR_CODES.NO_WORKSPACE:
            return "pbb-ai-error-no-workspace";
        case AI_ERROR_CODES.BAD_REQUEST:
            return "pbb-ai-error-400";
        case AI_ERROR_CODES.UNAUTHORIZED:
            return "pbb-ai-error-401";
        case AI_ERROR_CODES.FORBIDDEN:
            return "pbb-ai-error-403";
        case AI_ERROR_CODES.RATE_LIMITED:
            return "pbb-ai-error-429";
        case AI_ERROR_CODES.BAD_GATEWAY:
            return "pbb-ai-error-502";
        default:
            return "pbb-ai-error-generic";
    }
}
